import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Divider,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, RefreshCw, Trash2, Wrench } from "lucide-mui";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import type {
  MicroAppGoogleConnection,
  SheetInspectResult,
  SheetSourceTestResult,
} from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import NoSelectionPrompt from "../../../components/NoSelectionPrompt.tsx";
import { api } from "../../../api/client.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import EmptyState from "../../../components/EmptyState.tsx";
import Panel from "../../../components/Panel.tsx";
import RowActions from "../../../components/RowActions.tsx";
import StatusIcon, { StatusLabel } from "../../../components/StatusIcon.tsx";
import { formatNumber } from "../../../utils/format.ts";
import { useOrganization } from "../../../contexts/OrganizationContext.tsx";

const EMPTY_FORM = {
  slug: "",
  title: "",
  spreadsheet_id: "",
  // The Google connection the sheet is read with — always one of the caller's
  // own, except when editing a source someone else authorized.
  google_connection_id: "",
  // Default to granting every sheet: the admin registers a spreadsheet without
  // having to enumerate ranges. Turning this off reveals the ranges field.
  allow_all: true,
  ranges: "",
};
type SheetForm = typeof EMPTY_FORM;

/**
 * Derive the source slug from its title. The backend validates slugs against
 * `^[a-z0-9][a-z0-9-]{1,63}$`, so strip diacritics, fold anything else to a
 * dash and trim to that shape.
 */
function slugify(title: string): string {
  return title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/g, "");
}

/** Sentinel select value: leave for Google to connect another account. */
const CONNECT_NEW = "__connect_new__";
/** The half-filled dialog, kept across the Google consent redirect. */
const DRAFT_KEY = "microAppSheets.draft";

type Draft = { form: SheetForm; editingId: string | null };

function saveDraft(draft: Draft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Storage unavailable: the dialog simply reopens empty.
  }
}

function takeDraft(): Draft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    sessionStorage.removeItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}

function accountLabel(c: MicroAppGoogleConnection) {
  const who = c.is_mine ? "" : ` (${c.connected_by_name ?? "another user"})`;
  const state = c.status === "active" ? "" : " — needs reconnect";
  return `${c.google_account_email || "Google account"}${who}${state}`;
}

/** What testing a source found: the spreadsheet's tabs, and per allowed range whether it reads. */
function SheetTestResult({ result }: { result: SheetSourceTestResult }) {
  return (
    <Box sx={{ mt: 1.5 }}>
      {result.error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {result.error}
        </Alert>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {result.title} — tabs: {result.tabs.map((t) => t.title).join(", ")}
        </Typography>
      )}
      {result.warnings.map((w) => (
        <Alert key={w} severity="warning" sx={{ mt: 1 }}>
          {w}
        </Alert>
      ))}
      {result.results.map((r) => (
        <Stack key={r.range} direction="row" spacing={1} sx={{ mt: 1, alignItems: "flex-start" }}>
          <StatusIcon kind={r.ok ? "ok" : "error"} size={18} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
              {r.range}
            </Typography>
            {r.ok ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {formatNumber(r.rows)} rows · columns: {r.columns.slice(0, 6).join(", ")}
                {r.columns.length > 6 ? "…" : ""}
              </Typography>
            ) : (
              <Typography variant="caption" color="error" sx={{ display: "block" }}>
                {r.error}
              </Typography>
            )}
            {r.suggested_range && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block" }}>
                Row {r.suggested_header_row} looks like the header — consider{" "}
                <code>{r.suggested_range}</code>
              </Typography>
            )}
          </Box>
        </Stack>
      ))}
    </Box>
  );
}

/**
 * Connect Google accounts and register which ranges micro apps may read.
 *
 * The registry is the access control, not a convenience: a manifest names a
 * source slug and a range, and the range must match an entry here exactly. It
 * is deliberately not reachable over MCP, so an agent can write a manifest
 * against these sources but cannot add one.
 *
 * Each sheet is read with the Google account of whoever authorized it, so
 * several people can bring sheets from their own accounts. A sheet can only be
 * bound to an account you connected yourself.
 */
function MicroAppSheetsPanel() {
  const queryClient = useQueryClient();
  const { selectedOrgId, currentOrg } = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);
  // Set when the dialog is editing rather than creating. Editing exists so a
  // source can be repointed in place — converting an uploaded .xlsx to a real
  // Google Sheet produces a NEW spreadsheet id, and re-creating the source
  // would change the slug that published manifests already reference.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SheetForm>(EMPTY_FORM);
  // The connection the source had when the edit dialog opened — the only
  // not-yours account the dialog will offer.
  const [originalConnectionId, setOriginalConnectionId] = useState<string | null>(null);
  const [inspection, setInspection] = useState<SheetInspectResult | null>(null);
  const [testResult, setTestResult] = useState<SheetSourceTestResult | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: keys.microAppSheets.all });
  };

  const { data: setup } = useQuery({
    queryKey: keys.microAppSheets.setup(),
    queryFn: () => api.getMicroAppGoogleSetupInfo(),
  });

  const { data: connections, isLoading: connLoading } = useQuery({
    queryKey: keys.microAppSheets.connections(selectedOrgId),
    queryFn: () => api.getMicroAppGoogleConnections(selectedOrgId),
    enabled: !!selectedOrgId,
  });

  const myConnections = (connections ?? []).filter(
    (c) => c.organization_id === selectedOrgId && c.is_mine,
  );

  const { data: sources, isLoading: sourcesLoading } = useQuery({
    queryKey: keys.microAppSheets.sources(selectedOrgId),
    queryFn: () => api.getMicroAppSheetSources(selectedOrgId),
    enabled: !!selectedOrgId,
  });

  // Back from Google: reopen the dialog the user left, with the account they
  // just connected selected. A reconnect started from a sheet's row needs no
  // dialog — that sheet was rebound by the callback. The ref makes this run
  // once per return even when effects fire twice (StrictMode), since taking
  // the draft is destructive.
  const handledReturn = useRef<string | null>(null);
  useEffect(() => {
    if (searchParams.get("google") !== "connected") return;
    const key = searchParams.toString();
    if (handledReturn.current === key) return;
    handledReturn.current = key;
    const connectionId = searchParams.get("connection");
    if (searchParams.get("source")) {
      toast.success("Sheet re-authorized with your Google account");
    } else if (connectionId) {
      const draft = takeDraft();
      if (draft) {
        setEditingId(draft.editingId);
        setForm({ ...draft.form, google_connection_id: connectionId });
        setAddOpen(true);
      } else {
        toast.success("Google account connected");
      }
    }
    queryClient.invalidateQueries({ queryKey: keys.microAppSheets.all });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  const connectAnotherAccount = () => {
    if (!selectedOrgId) return;
    saveDraft({ form, editingId });
    window.location.href = api.microAppGoogleInstallUrl(selectedOrgId);
  };

  const createSource = useMutation({
    mutationFn: () => {
      // Ranges are matched by exact string equality, so trim aggressively —
      // a stray space would make a range silently unusable.
      const allowed_ranges = form.ranges
        .split("\n")
        .map((r) => r.trim())
        .filter(Boolean);
      if (editingId) {
        // Slug is intentionally not editable: it is the contract published
        // manifests reference.
        return api.updateMicroAppSheetSource(editingId, {
          title: form.title.trim(),
          spreadsheet_id: form.spreadsheet_id.trim(),
          // Only sent when changed: keeping a colleague's account is allowed,
          // choosing one is not.
          ...(form.google_connection_id && form.google_connection_id !== originalConnectionId
            ? { google_connection_id: form.google_connection_id }
            : {}),
          allow_all: form.allow_all,
          // When granting all sheets the backend auto-lists the tabs, so the
          // ranges field is ignored.
          ...(form.allow_all ? {} : { allowed_ranges }),
        });
      }
      return api.createMicroAppSheetSource({
        organization_id: selectedOrgId!,
        slug: form.slug.trim(),
        title: form.title.trim(),
        spreadsheet_id: form.spreadsheet_id.trim(),
        google_connection_id: form.google_connection_id,
        allow_all: form.allow_all,
        ...(form.allow_all ? {} : { allowed_ranges }),
      });
    },
    onSuccess: () => {
      toast.success(editingId ? "Sheet source saved" : "Sheet source added");
      closeDialog();
      invalidate();
    },
  });

  const inspect = useMutation({
    mutationFn: () =>
      api.inspectSpreadsheet(
        selectedOrgId!,
        form.google_connection_id,
        form.spreadsheet_id.trim(),
      ),
    onSuccess: (r) => {
      setInspection(r);
      // Inspect returns the id extracted from a pasted URL; adopt it so what
      // gets saved is what was verified.
      if (r.ok && r.spreadsheet_id) {
        setForm((f) => ({ ...f, spreadsheet_id: r.spreadsheet_id }));
      }
    },
  });

  const testSource = useMutation({
    mutationFn: (id: string) => api.testMicroAppSheetSource(id),
    onSuccess: (r) => setTestResult(r),
  });

  const closeDialog = () => {
    setAddOpen(false);
    setEditingId(null);
    setOriginalConnectionId(null);
    setInspection(null);
    setForm(EMPTY_FORM);
  };

  const openAdd = () => {
    // Preselect when there is exactly one sensible choice.
    const usable = myConnections.filter((c) => c.status === "active");
    setForm({ ...EMPTY_FORM, google_connection_id: usable.length === 1 ? usable[0].id : "" });
    setAddOpen(true);
  };

  const openEdit = (src: {
    id: string;
    slug: string;
    title: string;
    spreadsheet_id: string;
    google_connection_id: string | null;
    allow_all: boolean;
    allowed_ranges: string[];
  }) => {
    setEditingId(src.id);
    setOriginalConnectionId(src.google_connection_id);
    setInspection(null);
    setForm({
      slug: src.slug,
      title: src.title,
      spreadsheet_id: src.spreadsheet_id,
      google_connection_id: src.google_connection_id ?? "",
      allow_all: src.allow_all,
      // The "*" sentinel is not a real range — never surface it in the editor.
      ranges: src.allowed_ranges.filter((r) => r !== "*").join("\n"),
    });
    setAddOpen(true);
  };

  const deleteSource = useMutation({
    mutationFn: (id: string) => api.deleteMicroAppSheetSource(id),
    onSuccess: () => {
      invalidate();
      toast.success("Sheet source deleted");
    },
  });

  const disconnect = useMutation({
    mutationFn: (id: string) => api.disconnectMicroAppGoogle(id),
    onSuccess: () => {
      invalidate();
      toast.success("Google account disconnected");
    },
  });

  if (setup && !setup.configured) {
    return (
      <Alert severity="info" variant="outlined">
        Google Sheets is not configured on this deployment. Set the
        <code> GOOGLE_SHEETS_CLIENT_ID</code>, <code>GOOGLE_SHEETS_CLIENT_SECRET</code> and
        <code> GOOGLE_SHEETS_STATE_SECRET</code> environment variables to enable it.
      </Alert>
    );
  }

  if (!selectedOrgId || !currentOrg) return <NoSelectionPrompt kind="org" />;

  const orgConnections = (connections ?? []).filter((c) => c.organization_id === selectedOrgId);
  const connectionById = new Map(orgConnections.map((c) => [c.id, c]));
  const selectedConnection = connectionById.get(form.google_connection_id);
  // Inspecting reads through the connection, so the backend only allows it on
  // your own — a colleague's account can be kept on a source, not browsed.
  const canInspect = !!selectedConnection?.is_mine;
  // Changing the spreadsheet of a source read with someone else's account
  // would read a document they never registered; the backend refuses it.
  const spreadsheetLocked =
    !!editingId && !!selectedConnection && !selectedConnection.is_mine;

  return (
    <Stack spacing={3}>
      <Panel
        title="Your Google accounts"
        subtitle={
          <>
            Each sheet is read with the Google account of whoever added it, and everyone who can
            open a dashboard sees the sheet data it reads. You can only add sheets through accounts
            you connected yourself.
          </>
        }
        action={
          <Button
            size="small"
            variant={myConnections.length ? "outlined" : "contained"}
            href={api.microAppGoogleInstallUrl(selectedOrgId)}
          >
            Connect account
          </Button>
        }
        disablePadding
      >
        {connLoading ? (
          <CenteredSpinner size={20} />
        ) : myConnections.length === 0 ? (
          <EmptyState
            title="No Google account connected"
            description={`You have not connected a Google account in ${currentOrg.name} yet.`}
          />
        ) : (
          <Stack divider={<Divider />}>
            {myConnections.map((c) => (
              <Box key={c.id} sx={{ px: 2, py: 1.5 }}>
                <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                      {c.google_account_email || "Google account"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.source_count === 1 ? "1 sheet" : `${c.source_count} sheets`}
                    </Typography>
                  </Box>
                  {c.status === "active" ? (
                    <StatusLabel kind="ok" label="Active" />
                  ) : (
                    <StatusLabel kind="attention" label="Needs reconnect" />
                  )}
                  <Button
                    size="small"
                    variant={c.status === "active" ? "outlined" : "contained"}
                    href={api.microAppGoogleInstallUrl(selectedOrgId)}
                  >
                    Reconnect
                  </Button>
                  <RowActions
                    label={c.google_account_email || "Google account"}
                    actions={[
                      {
                        label: c.source_count
                          ? "Disconnect (re-authorize or delete its sheets first)"
                          : "Disconnect",
                        icon: <Trash2 fontSize="small" />,
                        destructive: true,
                        disabled: c.source_count > 0,
                        onClick: () => disconnect.mutateAsync(c.id),
                        confirm: {
                          title: `Disconnect Google account: ${accountLabel(c)}`,
                          body: (
                            <>
                              Hive forgets the connection; to revoke its access everywhere, remove
                              Hive in the Google account's settings.
                            </>
                          ),
                          confirmLabel: "Disconnect",
                        },
                      },
                    ]}
                  />
                </Stack>
                {c.status !== "active" && c.last_error ? (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                    {c.last_error}
                  </Alert>
                ) : null}
              </Box>
            ))}
          </Stack>
        )}
      </Panel>

      <Panel
        title="Sheet sources"
        subtitle={
          <>
            A micro app references a source by slug and may read only the ranges listed here,
            matched exactly. The spreadsheet must be shared with the Google account the source is
            read with.
          </>
        }
        action={
          <Button size="small" variant="outlined" onClick={openAdd}>
            Add source
          </Button>
        }
        disablePadding
      >
        {sourcesLoading ? (
          <CenteredSpinner size={20} />
        ) : (sources ?? []).length === 0 ? (
          <EmptyState
            title="No sheet sources yet"
            description="Add a source to let micro apps read a spreadsheet."
          />
        ) : (
          <Stack divider={<Divider />}>
            {(sources ?? []).map((s) => {
              const sc = s.google_connection_id
                ? connectionById.get(s.google_connection_id)
                : null;
              const healthy = sc?.status === "active";
              return (
                <Box key={s.id} sx={{ px: 2, py: 1.5 }}>
                  <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                        <Typography variant="subtitle2">{s.title}</Typography>
                        <Chip
                          size="small"
                          label={s.slug}
                          variant="outlined"
                          sx={{ fontFamily: "monospace" }}
                        />
                        {sc ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={healthy ? "default" : "warning"}
                            label={`via ${accountLabel(sc)}`}
                          />
                        ) : (
                          <Chip size="small" color="warning" label="Not authorized" />
                        )}
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", fontFamily: "monospace" }}
                      >
                        {s.spreadsheet_id}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                        {s.allow_all ? (
                          <Chip size="small" color="primary" label="All sheets" />
                        ) : null}
                        {s.allowed_ranges
                          .filter((r) => r !== "*")
                          .map((r) => (
                            <Chip key={r} size="small" label={r} />
                          ))}
                      </Stack>
                      {testingId === s.id && testSource.isPending && (
                        <CircularProgress size={16} sx={{ mt: 1 }} />
                      )}
                      {testingId === s.id && testResult && <SheetTestResult result={testResult} />}
                    </Box>
                    {!healthy ? (
                      <Tooltip title="Read this sheet with a Google account of yours from now on">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<RefreshCw fontSize="small" />}
                          href={api.microAppGoogleInstallUrl(selectedOrgId, s.id)}
                        >
                          Re-authorize
                        </Button>
                      </Tooltip>
                    ) : null}
                    <RowActions
                      label={s.title}
                      primary={{
                        label: "Test every allowed range against Google",
                        icon: <Wrench fontSize="small" />,
                        disabled: testSource.isPending,
                        onClick: () => {
                          setTestingId(s.id);
                          setTestResult(null);
                          testSource.mutate(s.id);
                        },
                      }}
                      actions={[
                        {
                          label: "Edit source",
                          icon: <Pencil fontSize="small" />,
                          onClick: () => openEdit(s),
                        },
                        {
                          label: "Delete source",
                          icon: <Trash2 fontSize="small" />,
                          destructive: true,
                          onClick: () => deleteSource.mutateAsync(s.id),
                          confirm: {
                            title: `Delete sheet source: ${s.title}`,
                            body: (
                              <>
                                Published apps that read <code>{s.slug}</code> stop getting its
                                data.
                              </>
                            ),
                            confirmLabel: "Delete",
                          },
                        },
                      ]}
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Panel>

      <Dialog open={addOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Edit sheet source" : "Add sheet source"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={form.title}
              // The slug follows the title while creating; once created it is
              // frozen, so editing a title never moves a published reference.
              onChange={(e) =>
                setForm({
                  ...form,
                  title: e.target.value,
                  slug: editingId ? form.slug : slugify(e.target.value),
                })
              }
              size="small"
              autoFocus
            />
            <TextField
              label="Slug"
              error={!editingId && !!form.title.trim() && form.slug.length < 2}
              helperText={
                editingId
                  ? "Fixed — published manifests reference this slug"
                  : !!form.title.trim() && form.slug.length < 2
                    ? "Title needs at least two letters or digits"
                    : "Derived from the title. Referenced from a manifest as sheet_source"
              }
              value={form.slug}
              size="small"
              disabled
            />
            <TextField
              select
              label="Read with Google account"
              value={form.google_connection_id}
              onChange={(e) => {
                if (e.target.value === CONNECT_NEW) {
                  connectAnotherAccount();
                  return;
                }
                setInspection(null);
                setForm({ ...form, google_connection_id: e.target.value });
              }}
              helperText={
                spreadsheetLocked
                  ? "Read with a colleague's account. Choose one of yours to change the spreadsheet."
                  : "The spreadsheet must be shared with this account"
              }
              size="small"
            >
              {/* A colleague's account stays selectable only on the source it already backs. */}
              {orgConnections
                .filter((c) => c.is_mine || c.id === originalConnectionId)
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {accountLabel(c)}
                  </MenuItem>
                ))}
              <MenuItem value={CONNECT_NEW}>Connect another Google account…</MenuItem>
            </TextField>
            <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
              <TextField
                label="Spreadsheet URL or ID"
                helperText="Paste the whole sheet URL — the id is extracted for you"
                value={form.spreadsheet_id}
                onChange={(e) => setForm({ ...form, spreadsheet_id: e.target.value })}
                size="small"
                sx={{ flex: 1 }}
                disabled={spreadsheetLocked}
              />
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 0.5 }}
                onClick={() => inspect.mutate()}
                loading={inspect.isPending}
                disabled={!form.spreadsheet_id.trim() || !canInspect}
              >
                Check
              </Button>
            </Stack>
            {inspection && !inspection.ok && (
              <Alert severity="error">{inspection.error}</Alert>
            )}
            {inspection?.ok && (
              <Alert severity="success">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {inspection.title}
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  {form.allow_all
                    ? "Tabs found (all readable):"
                    : "Click a tab to add its range:"}
                </Typography>
                <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  {inspection.tabs.map((t) => (
                    <Chip
                      key={t.title}
                      size="small"
                      label={`${t.title} (${t.rows}×${t.columns})`}
                      onClick={
                        form.allow_all
                          ? undefined
                          : () =>
                        setForm((f) => ({
                          ...f,
                          ranges: [
                            ...f.ranges.split("\n").map((r) => r.trim()).filter(Boolean),
                            t.suggested_range,
                          ]
                            .filter((r, i, a) => a.indexOf(r) === i)
                            .join("\n"),
                        }))
                      }
                    />
                  ))}
                </Stack>
              </Alert>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={form.allow_all}
                  onChange={(e) => setForm({ ...form, allow_all: e.target.checked })}
                />
              }
              label="Allow all sheets"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              {form.allow_all
                ? "Micro apps may read any range in this spreadsheet. Its tabs are listed automatically — no need to enter ranges."
                : "Restrict access to the specific ranges below."}
            </Typography>
            {form.allow_all ? null : (
              <TextField
                label="Allowed ranges"
                helperText="One A1 range per line, e.g. Targets!A1:D40. Matched exactly."
                value={form.ranges}
                onChange={(e) => setForm({ ...form, ranges: e.target.value })}
                multiline
                minRows={3}
                size="small"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createSource.mutate()}
            loading={createSource.isPending}
            disabled={
              form.slug.trim().length < 2 ||
              !form.title.trim() ||
              !form.spreadsheet_id.trim() ||
              !form.google_connection_id ||
              (!form.allow_all && !form.ranges.trim())
            }
          >
            {editingId ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

    </Stack>
  );
}

export default MicroAppSheetsPanel;
